import React, { useState } from 'react';
import { X, Download, FileSpreadsheet, FileText, CheckSquare, Square } from 'lucide-react';
import * as XLSX from 'xlsx';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable'; // ✅ import nommé, pas side-effect

const HangarLeaseExportModal = ({ isOpen, onClose, data, onExport }) => {
  const [exportOptions, setExportOptions] = useState({
    hangars: true,
    leases: true,
    financial: true,
    alerts: false
  });
  const [exportType, setExportType] = useState('xlsx');
  const [exporting, setExporting] = useState(false);

  if (!isOpen) return null;

  const toggleOption = (option) => {
    setExportOptions(prev => ({ ...prev, [option]: !prev[option] }));
  };

  const prepareHangarsData = () => {
    return data.hangars.map(h => ({
      'Hangar Number': h.hangar_number,
      'Name': h.name || '',
      'Location': h.location || '',
      'Surface (sqft)': h.surface_sqft,
      'Status': h.status,
      'Monthly Rent ($)': h.monthly_rent,
      'Current Tenant': h.current_tenant_name || '',
      'Lease End Date': h.lease_end_date || '',
      'Rent Arrears ($)': h.rent_arrears || 0,
      'Security Deposit ($)': h.security_deposit || 0,
      'Insurance Expiry': h.insurance_expiry_date || '',
      'Local Tax Due': h.local_tax_due_date || '',
      'Local Tax Paid': h.local_tax_paid ? 'Yes' : 'No'
    }));
  };

  const prepareLeasesData = () => {
    return data.leases.map(l => ({
      'Lease Number': l.lease_number,
      'Tenant': l.tenant_name || '',
      'Hangar': l.hangar_number || '',
      'Start Date': l.start_date,
      'End Date': l.end_date || 'Open-ended',
      'Monthly Rent ($)': l.monthly_rent,
      'Ground Rent ($)': l.ground_rent || 0,
      'Security Deposit ($)': l.security_deposit || 0,
      'Lease Type': l.lease_type || 'active',
      'Status': l.status,
      'Last Rent Paid': l.last_rent_paid_date || 'Never',
      'Rent Arrears ($)': l.rent_arrears || 0,
      'Insurance Expiry': l.insurance_expiry_date || '',
      'Insurance Provider': l.insurance_provider || '',
      'Policy Number': l.insurance_policy_number || ''
    }));
  };

  const prepareFinancialData = () => {
    return [
      { 'Metric': 'Report Generated',              'Value': new Date().toLocaleString() },
      { 'Metric': '--- HANGAR STATS ---',           'Value': '' },
      { 'Metric': 'Total Hangars',                  'Value': data.stats?.totalHangars || 0 },
      { 'Metric': 'Occupied Hangars',               'Value': data.stats?.occupiedHangars || 0 },
      { 'Metric': 'Available Hangars',              'Value': data.stats?.availableHangars || 0 },
      { 'Metric': 'Occupancy Rate',                 'Value': `${data.stats?.occupancyRate || 0}%` },
      { 'Metric': 'Total Surface Area',             'Value': `${(data.stats?.totalSurface || 0).toLocaleString()} sqft` },
      { 'Metric': 'Leased Surface Area',            'Value': `${(data.stats?.leasedSurface || 0).toLocaleString()} sqft` },
      { 'Metric': '--- FINANCIAL STATS ---',        'Value': '' },
      { 'Metric': 'Monthly Rental Revenue',         'Value': `$${(data.stats?.monthlyRevenue || 0).toLocaleString()}` },
      { 'Metric': 'Total Security Deposits Held',   'Value': `$${(data.stats?.totalDeposits || 0).toLocaleString()}` },
      { 'Metric': 'Total Rent Arrears',             'Value': `$${(data.stats?.totalArrears || 0).toLocaleString()}` },
      { 'Metric': '--- LEASE STATS ---',            'Value': '' },
      { 'Metric': 'Active Leases',                  'Value': data.stats?.activeLeases || 0 },
      { 'Metric': 'Expiring Leases (30 days)',       'Value': data.stats?.expiringLeasesCount || 0 },
      { 'Metric': 'Expiring Insurances (60 days)',   'Value': data.stats?.expiringInsurancesCount || 0 },
      { 'Metric': 'Leases in Arrears',              'Value': data.stats?.leasesInArrears || 0 },
      { 'Metric': '--- ALERTS ---',                 'Value': '' },
      { 'Metric': 'Rent Arrears Alerts',            'Value': data.stats?.rentArrearsCount || 0 },
      { 'Metric': 'Expiring Lease Alerts',          'Value': data.stats?.expiringLeasesCount || 0 },
      { 'Metric': 'Insurance Expiry Alerts',        'Value': data.stats?.expiringInsurancesCount || 0 },
      { 'Metric': 'Unpaid Tax Alerts',              'Value': data.stats?.unpaidTaxesCount || 0 }
    ];
  };

  const prepareAlertsData = () => {
    const alerts = [];

    if (data.rentArrears?.length) {
      data.rentArrears.forEach(a => {
        alerts.push({
          'Alert Type': 'Rent Arrears',
          'Tenant': a.tenant_name,
          'Hangar': a.hangar_number,
          'Amount Due ($)': a.amount,
          'Status': 'Urgent'
        });
      });
    }

    if (data.expiringLeases?.length) {
      data.expiringLeases.forEach(l => {
        alerts.push({
          'Alert Type': 'Lease Expiring',
          'Tenant': l.tenant_name,
          'Hangar': l.hangar_number,
          'Expiry Date': l.end_date,
          'Days Left': l.days_left,
          'Status': l.days_left <= 7 ? 'Critical' : 'Warning'
        });
      });
    }

    if (data.expiringInsurances?.length) {
      data.expiringInsurances.forEach(i => {
        alerts.push({
          'Alert Type': 'Insurance Expiring',
          'Tenant': i.tenant_name,
          'Hangar': i.hangar_number,
          'Expiry Date': i.insurance_expiry_date,
          'Days Left': i.days_left,
          'Status': i.days_left <= 30 ? 'Critical' : 'Warning'
        });
      });
    }

    if (data.unpaidTaxes?.length) {
      data.unpaidTaxes.forEach(t => {
        alerts.push({
          'Alert Type': 'Unpaid Local Tax',
          'Hangar': t.hangar_number,
          'Amount Due ($)': t.local_tax_amount,
          'Due Date': t.local_tax_due_date,
          'Status': 'Urgent'
        });
      });
    }

    return alerts;
  };

  const exportToXLSX = () => {
    setExporting(true);
    const workbook = XLSX.utils.book_new();

    if (exportOptions.hangars && data.hangars?.length) {
      const hangarsSheet = XLSX.utils.json_to_sheet(prepareHangarsData());
      XLSX.utils.book_append_sheet(workbook, hangarsSheet, 'Hangars');
    }

    if (exportOptions.leases && data.leases?.length) {
      const leasesSheet = XLSX.utils.json_to_sheet(prepareLeasesData());
      XLSX.utils.book_append_sheet(workbook, leasesSheet, 'Active Leases');
    }

    if (exportOptions.financial) {
      const financialSheet = XLSX.utils.json_to_sheet(prepareFinancialData());
      XLSX.utils.book_append_sheet(workbook, financialSheet, 'Financial Summary');
    }

    if (exportOptions.alerts && (data.rentArrears?.length || data.expiringLeases?.length)) {
      const alertsSheet = XLSX.utils.json_to_sheet(prepareAlertsData());
      XLSX.utils.book_append_sheet(workbook, alertsSheet, 'Alerts');
    }

    XLSX.writeFile(workbook, `hangar_lease_report_${new Date().toISOString().split('T')[0]}.xlsx`);
    setExporting(false);
    onExport?.();
    onClose();
  };

  const exportToPDF = () => {
    setExporting(true);
    const doc = new jsPDF('landscape');
    let yOffset = 20;

    // Titre
    doc.setFontSize(18);
    doc.setTextColor(0, 100, 0);
    doc.text('Hangar & Lease Analytics Report', 14, yOffset);
    yOffset += 8;

    doc.setFontSize(10);
    doc.setTextColor(100, 100, 100);
    doc.text(`Generated: ${new Date().toLocaleString()}`, 14, yOffset);
    yOffset += 10;

    // ── Hangars ──────────────────────────────────────────────────────────────
    if (exportOptions.hangars && data.hangars?.length) {
      doc.setFontSize(14);
      doc.setTextColor(0, 0, 0);
      doc.text('Hangars', 14, yOffset);
      yOffset += 6;

      const hangarsForPDF = prepareHangarsData().slice(0, 20);
      autoTable(doc, {                                   // ✅ autoTable(doc, {...})
        startY: yOffset,
        head: [Object.keys(hangarsForPDF[0] || {})],
        body: hangarsForPDF.map(h => Object.values(h)),
        theme: 'striped',
        styles: { fontSize: 8, cellPadding: 2 },
        headStyles: { fillColor: [50, 50, 80], textColor: 255, fontSize: 8 }
      });
      yOffset = doc.lastAutoTable.finalY + 10;
    }

    // ── Leases ───────────────────────────────────────────────────────────────
    if (exportOptions.leases && data.leases?.length) {
      if (yOffset > 180) { doc.addPage(); yOffset = 20; }

      doc.setFontSize(14);
      doc.setTextColor(0, 0, 0);
      doc.text('Active Leases', 14, yOffset);
      yOffset += 6;

      const leasesForPDF = prepareLeasesData().slice(0, 20);
      autoTable(doc, {                                   // ✅
        startY: yOffset,
        head: [Object.keys(leasesForPDF[0] || {})],
        body: leasesForPDF.map(l => Object.values(l)),
        theme: 'striped',
        styles: { fontSize: 7, cellPadding: 2 },
        headStyles: { fillColor: [50, 50, 80], textColor: 255, fontSize: 7 }
      });
      yOffset = doc.lastAutoTable.finalY + 10;
    }

    // ── Financial Summary ─────────────────────────────────────────────────────
    if (exportOptions.financial) {
      if (yOffset > 180) { doc.addPage(); yOffset = 20; }

      doc.setFontSize(14);
      doc.setTextColor(0, 0, 0);
      doc.text('Financial Summary', 14, yOffset);
      yOffset += 6;

      const financialForPDF = prepareFinancialData();
      autoTable(doc, {                                   // ✅
        startY: yOffset,
        body: financialForPDF.map(f => [f.Metric, f.Value]),
        theme: 'striped',
        styles: { fontSize: 9, cellPadding: 3 },
        columnStyles: { 0: { cellWidth: 80 }, 1: { cellWidth: 60 } }
      });
      yOffset = doc.lastAutoTable.finalY + 10;
    }

    // ── Alerts ────────────────────────────────────────────────────────────────
    if (exportOptions.alerts && prepareAlertsData().length > 0) {
      if (yOffset > 180) { doc.addPage(); yOffset = 20; }

      doc.setFontSize(14);
      doc.setTextColor(0, 0, 0);
      doc.text('Alerts', 14, yOffset);
      yOffset += 6;

      const alertsForPDF = prepareAlertsData();
      autoTable(doc, {                                   // ✅
        startY: yOffset,
        head: [Object.keys(alertsForPDF[0] || {})],
        body: alertsForPDF.map(a => Object.values(a)),
        theme: 'striped',
        styles: { fontSize: 8, cellPadding: 2 },
        headStyles: { fillColor: [150, 30, 30], textColor: 255, fontSize: 8 }
      });
    }

    doc.save(`hangar_lease_report_${new Date().toISOString().split('T')[0]}.pdf`);
    setExporting(false);
    onExport?.();
    onClose();
  };

  const handleExport = () => {
    if (exportType === 'xlsx') {
      exportToXLSX();
    } else {
      exportToPDF();
    }
  };

  const nothingSelected = !exportOptions.hangars && !exportOptions.leases &&
                          !exportOptions.financial && !exportOptions.alerts;

  return (
    <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50">
      <div className="bg-gray-800 border border-gray-700 rounded-xl w-full max-w-md shadow-xl">
        <div className="flex items-center justify-between p-4 border-b border-gray-700">
          <h3 className="text-white font-semibold flex items-center gap-2">
            <Download size={18} className="text-emerald-400" />
            Export Report
          </h3>
          <button onClick={onClose} className="text-gray-400 hover:text-white transition-colors">
            <X size={18} />
          </button>
        </div>

        <div className="p-4 space-y-4">
          {/* Format */}
          <div>
            <label className="text-gray-400 text-xs block mb-2">Export Format</label>
            <div className="flex gap-3">
              <button
                onClick={() => setExportType('xlsx')}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm transition-colors ${
                  exportType === 'xlsx'
                    ? 'bg-emerald-500/20 text-emerald-400 border border-emerald-500/30'
                    : 'bg-gray-700/50 text-gray-400 border border-gray-600 hover:text-white'
                }`}
              >
                <FileSpreadsheet size={16} />
                Excel (XLSX)
              </button>
              <button
                onClick={() => setExportType('pdf')}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm transition-colors ${
                  exportType === 'pdf'
                    ? 'bg-emerald-500/20 text-emerald-400 border border-emerald-500/30'
                    : 'bg-gray-700/50 text-gray-400 border border-gray-600 hover:text-white'
                }`}
              >
                <FileText size={16} />
                PDF
              </button>
            </div>
          </div>

          {/* Contenu */}
          <div>
            <label className="text-gray-400 text-xs block mb-2">Include in Report</label>
            <div className="space-y-2">
              {[
                { key: 'hangars',   label: 'Hangars List',       sub: `${data.hangars?.length || 0} records` },
                { key: 'leases',    label: 'Active Leases',      sub: `${data.leases?.length || 0} records` },
                { key: 'financial', label: 'Financial Summary',  sub: 'KPIs & Stats' },
                { key: 'alerts',    label: 'Alerts',             sub: 'Arrears & Expirations' },
              ].map(({ key, label, sub }) => (
                <button
                  key={key}
                  onClick={() => toggleOption(key)}
                  className="flex items-center gap-2 w-full px-3 py-2 rounded-lg bg-gray-700/30 hover:bg-gray-700/50 transition-colors"
                >
                  {exportOptions[key]
                    ? <CheckSquare size={16} className="text-emerald-400" />
                    : <Square size={16} className="text-gray-500" />}
                  <span className="text-sm text-gray-300 flex-1 text-left">{label}</span>
                  <span className="text-xs text-gray-500">{sub}</span>
                </button>
              ))}
            </div>
          </div>
        </div>

        <div className="flex justify-end gap-3 p-4 border-t border-gray-700">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm text-gray-400 hover:text-white transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={handleExport}
            disabled={exporting || nothingSelected}
            className={`px-4 py-2 rounded-lg text-sm font-medium flex items-center gap-2 transition-colors ${
              exporting || nothingSelected
                ? 'bg-gray-600 text-gray-400 cursor-not-allowed'
                : 'bg-emerald-600 text-white hover:bg-emerald-700'
            }`}
          >
            {exporting ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white" />
                Exporting...
              </>
            ) : (
              <>
                <Download size={14} />
                Export {exportType.toUpperCase()}
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default HangarLeaseExportModal;